import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { decodeAnsi } from "./ansi";
import type { Sgr as AnsiSgr, Span as AnsiSpan } from "./ansi";
import { TermRow } from "./term-row";

// Replaying a terminal session, as a component rather than as a thing every
// page that shows a terminal writes again.
//
// The claim a replay makes is "what you are looking at is a session
// happening, not a screenshot of one", and four surfaces in this repo make it:
// herdr types command lines behind a blinking block caret, pi replays a
// recorded session into a parchment terminal, fx shows a prompt waiting, and
// tsh.lona.so needs to show a shell mounting its command groups. The arithmetic
// of making that claim — when to start, what to type, what to print, when to
// stop — is motion mechanics, and it is the same arithmetic every time.
//
// It lives HERE, in the brand-less component layer, for a specific reason: the
// engine previously sat in `@tento-brand/kit`, which `lona/so.lona` has no path
// mapping for and should not grow one for ahead of its own migration. A
// mechanism two independent products need is the definition of a primitive, so
// the primitive moved down rather than the dependency moving sideways.
//
// Nothing here decides how a line LOOKS. The component ships a plain row and
// declares no colour, no face and no size — it inherits all three — and a
// caller that wants its own row element passes `$line`. That is what lets pi
// stay warm ink on parchment and herdr stay Catppuccin on near-black while the
// beats they arrive on are one implementation.
//
// The line classification is the interesting half and it is deliberately
// syntactic. A session has three kinds of line:
//
//   $ …  or  > …   something a person typed  ->  arrives a character at a time
//   anything else   something a program printed  ->  arrives whole
//   (empty)         structure  ->  arrives instantly, with a short beat
//
// so a transcript needs no timing metadata and no authoring format: the same
// plain string a static figure prints is enough to replay it. That is the
// property worth protecting if this ever grows — the moment a model has to
// carry frame timings, the transcript stops being copy and becomes a
// recording, and every page that shows one needs a recorder.
@component({ name: "std-session-replay" })
export class SessionReplay extends LonaWebComponent {
  private $screen: HTMLElement = this.$("screen");
  private $caret: HTMLElement = this.$("caret");
  private $prompt: HTMLElement = this.$("prompt");
  private $promptLabel: HTMLElement = this.$("prompt-label");
  private $promptInput: HTMLElement = this.$("prompt-input");
  private engine: Option<SessionReplay.Engine> = null;
  private $line: Option<(line: SessionReplay.Line) => HTMLElement> = null;

  static makeWith(vm: SessionReplay.ViewModel): SessionReplay {
    const $replay = SessionReplay.make();
    $replay.bind(vm);
    return $replay;
  }

  bind(vm: SessionReplay.ViewModel): void {
    this.$line = vm.$line;
    this.engine?.stop();
    this.engine = new SessionReplay.Engine(
      this.$screen,
      this.$caret,
      (line) => (this.$line ? this.$line(line) : this.$defaultRow(line)),
      vm.timing ?? SessionReplay.DEFAULT_TIMING,
    );
    // Recomputed once the transcript has finished arriving: whether it
    // overflows decides whether the screen is a tab stop at all.
    this.engine.onSettled = () => this.syncOverflow();

    // The prompt, if the caller wants one, is a row OUTSIDE the scrolling
    // screen — which is what makes this shaped like the TUI rather than like a
    // log. In tsh the prompt is pinned to the bottom of the pane and output
    // accumulates above it; a replay that types its command inline, in the
    // middle of the output, is showing a shell that does not exist.
    //
    // So a typed line is typed INTO the pinned row, and only joins the screen
    // above once it has been submitted.
    const prompt = vm.prompt ?? null;
    this.$prompt.toggleAttribute("hidden", !prompt);
    this.$promptLabel.textContent = prompt ?? "";
    this.$promptInput.textContent = "";
    this.style.setProperty("--session-replay-prompt-rows", prompt ? "1" : "0");
    this.engine.$promptInput = prompt ? this.$promptInput : null;

    this.engine.bind(vm.sample);
    this.resetOverflow();
    // The row count goes out as a custom property so the SCREEN can size itself
    // to the finished transcript before the first character arrives.
    //
    // Without it a replay grows a row at a time, and because these figures sit
    // in normal page flow every row shoves everything below it down — the
    // reader is trying to follow a session while the paragraph they were
    // reading walks off the bottom of the screen. Reserving the height makes
    // the panel a fixed hole in the page that fills up, which is what a
    // terminal actually looks like.
    this.style.setProperty("--session-replay-rows", String(this.engine.lineCount));
    // Gaps are reserved with the rows, for the same reason: a panel sized to
    // its row count alone would push its last line out the moment air appeared
    // between two commands.
    this.style.setProperty("--session-replay-gaps", String(this.engine.gapCount));
    // Armed by connectedCallback rather than here: a page engine binds a
    // section while it is still detached and then moves it into the shell,
    // and a DOM move is a remove plus an insert.
    if (this.isConnected) this.engine.arm(this);
  }

  /** How many rows the transcript will occupy, for callers reserving height. */
  get lineCount(): number {
    return this.engine?.lineCount ?? 0;
  }

  private $defaultRow(line: SessionReplay.Line): HTMLElement {
    const $row = document.createElement("div");
    $row.className = "line";
    if (line.mode === "typed") $row.classList.add("typed");
    if (line.mode === "blank") $row.classList.add("blank");
    SessionReplay.paintInto($row, line);
    return $row;
  }

  /**
   * Write a line's spans into an element, colour and all.
   *
   * Static and exported through the namespace because a caller supplying its
   * own `$line` still wants the colour decoding — the point of `$line` is to
   * choose the ELEMENT and its layout, not to re-implement SGR.
   */
  static paintInto($el: HTMLElement, line: SessionReplay.Line): void {
    // The whole painter — the row ground hoist, the plain-run fast path, the
    // span styling — moved down into the term-row primitive, where the spool
    // workbench's term blocks and any other terminal surface share it. A line
    // is structurally a row (text, spans, ground) plus the replay's own mode,
    // which the painter has never needed.
    TermRow.paintInto($el, line);
  }

  /**
   * Make the screen focusable only when there is something to scroll.
   *
   * A panel whose transcript fits is not interactive, and giving every one of
   * them a tab stop would put four dead stops in the keyboard path of a page
   * with four figures. The measurement has to happen after the last line
   * lands, which is why the engine calls back rather than the component
   * guessing from the row count: the cap is expressed in CSS and the component
   * does not know it.
   */
  private syncOverflow(): void {
    $$.measure(() => {
      const $s = this.$screen;
      const overflows =
        $s.scrollHeight > $s.clientHeight + 1 || $s.scrollWidth > $s.clientWidth + 1;
      $$.mutate(() => {
        $s.dataset.overflowing = String(overflows);
        if (overflows) $s.setAttribute("tabindex", "0");
        else $s.removeAttribute("tabindex");
      });
    });
  }

  private resetOverflow(): void {
    delete this.$screen.dataset.overflowing;
    this.$screen.removeAttribute("tabindex");
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.engine?.arm(this);
  }

  // Both exits stop the engine, for the reason the original learned the
  // expensive way: a pooled component holding a pending timeout keeps writing
  // into a detached screen, and disconnected and recycled are different
  // lifecycles that have each leaked separately in this codebase.
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.engine?.stop();
  }

  onRecycle(): void {
    super.onRecycle();
    this.engine?.stop();
    this.engine = null;
    this.$line = null;
    this.$screen.replaceChildren();
    this.$caret.toggleAttribute("hidden", true);
    this.$promptInput.textContent = "";
    this.$prompt.toggleAttribute("hidden", true);
    this.resetOverflow();
  }

  static $styles = [
    css`
      :host {
        display: block;
      }

      /* The component paints no colour, no face and no size: a terminal's look
         belongs to the design showing it, and every one of these inherits. */

      /*
         The screen's OUTER height is fixed, and that is the whole layout-shift
         story.

         An earlier cut reserved the content height with min-height and capped
         it with max-height. That kept the finished panel the right size but
         still shifted the page mid-replay, because a scrollbar is not free: the
         moment the transcript passed the cap a vertical scrollbar appeared, and
         the moment a line wider than the box landed a HORIZONTAL one appeared
         and took its thickness out of the block axis, growing the element and
         pushing every section below it down. The shift arrived exactly when the
         replay got interesting.

         A single fixed height plus border-box sizing removes the whole class:
         scrollbars are laid out inside the border box, so whatever appears eats
         into the content area instead of adding to the element. The page cannot
         move, by construction rather than by measurement.

         rows * 1lh is exact — one row is one line box, because the screen is
         white-space: pre and never wraps — and one scrollbar thickness is added
         so that a horizontal scrollbar, when it appears, does not cover the
         last line of the transcript. */
      #screen {
        box-sizing: border-box;

        /* Never wrapped for a terminal capture: a wrapped column is a wrong
           column, and the alignment IS the content. A caller showing something
           that is not a transcript — a config file, where a long line wrapping
           is what an editor would do anyway — can ask for pre-wrap instead.
           Note the height reservation counts SOURCE lines, so a caller that
           opts into wrapping should expect a wrapped panel to scroll by however
           many lines actually wrapped. */
        white-space: var(--session-replay-wrap, pre);

        /* The inset belongs INSIDE the scrollport, never on the host.

           Declared on the host it insets the scroll container itself, and all
           three of the box's edges then lie a gutter short of the panel: the
           transcript clips early, the fade begins a gutter above the border,
           and a focused panel draws its scrollbar floating a gutter in from
           the edge. That reads as a margin around a small box rather than as a
           screen that fills its panel — and the clipping looks like a bug
           because content vanishes before it reaches an edge.

           Moved in here, the box spans the panel edge to edge and the TEXT is
           what is inset, so a long line scrolls out under the full width and
           the scrollbar rides the true border. box-sizing is border-box, so
           the height below must carry the block padding explicitly or the row
           cap would quietly lose two rows to it. */
        padding-inline: var(--session-replay-padding-inline, 0px);
        padding-block: var(--session-replay-padding-block, 0px);
        height: calc(
          min(var(--session-replay-rows, 0), var(--session-replay-max-rows, 9999)) * 1lh
          + var(--session-replay-scrollbar-size, 8px)
          + 2 * var(--session-replay-padding-block, 0px)
          + var(--session-replay-gaps, 0) * var(--session-replay-band-gap, 0px)
        );

        /* Not scrollable until asked for. A figure sitting in the middle of an
           article is something the reader scrolls PAST far more often than they
           scroll THROUGH, and a wheel over a scrollable box is captured by it —
           so the default swallows the gesture and the page stops moving under
           the reader's hand.

           overflow: hidden is the right off switch rather than clip, because a
           hidden box is still a scroll container: the replay keeps following
           its own caret through scrollTop while the reader cannot scroll it by
           hand. clip would break the follow. */
        overflow: hidden;
      }

      /* Focus is the opt-in, and it is only reachable when there is something
         to scroll — syncOverflow adds the tabindex, so click and keyboard both
         arrive here and a panel that fits is never a tab stop. Nothing about
         the box changes size: the height above is fixed, so handing the reader
         a scrollbar costs no layout. */
      #screen:focus {
        overflow: auto;

        /* Scroll chaining is only worth blocking once the reader has actually
           opted into scrolling this panel. Declared unconditionally it applies
           while the screen is overflow: hidden too — a hidden box is still a
           scroll container, so the wheel targets it, it refuses to move, and
           contain stops the gesture from reaching the page. The reader feels
           the article seize up under the pointer. Here it means what it says:
           a focused transcript scrolled to its end does not then throw the
           page. */
        overscroll-behavior: contain;
      }

      #screen:focus-visible {
        outline: var(--session-replay-focus-outline, 2px solid currentColor);
        outline-offset: -2px;
      }

      /* The affordance for the off state. With no scrollbar showing there is
         nothing to say the transcript continues, so an overflowing screen fades
         its last line out — and stops fading the moment it is focused and the
         real scrollbar takes over the job. */
      #screen[data-overflowing="true"]:not(:focus) {
        mask-image: linear-gradient(to bottom, #000 calc(100% - 1.5lh), transparent 100%);
      }

      /* The scrollbar itself. Sized and coloured through custom properties that
         default to currentColor, so the component still declares no palette —
         a caller that sets nothing gets the ink it already inherits. */
      #screen::-webkit-scrollbar {
        width: var(--session-replay-scrollbar-size, 8px);
        height: var(--session-replay-scrollbar-size, 8px);
      }

      #screen::-webkit-scrollbar-track {
        background: var(--session-replay-scrollbar-track, transparent);
      }

      #screen::-webkit-scrollbar-thumb {
        background: var(--session-replay-scrollbar-thumb, currentColor);
        border-radius: calc(var(--session-replay-scrollbar-size, 8px) / 2);
      }

      #screen::-webkit-scrollbar-thumb:hover {
        background: var(--session-replay-scrollbar-thumb-hover, currentColor);
      }

      #screen::-webkit-scrollbar-corner {
        background: transparent;
      }

      /* Firefox gets the standard properties, and ONLY Firefox.
         Chrome honours one scrollbar API or the other, never both: setting
         scrollbar-width makes it ignore every ::-webkit-scrollbar rule above,
         which is how a fully styled scrollbar renders as no scrollbar at all.
         So the standard declarations are fenced behind engines that do not
         understand the pseudo-element, and the two never meet. */
      @supports not selector(::-webkit-scrollbar) {
        #screen {
          scrollbar-width: thin;
          scrollbar-color: var(--session-replay-scrollbar-thumb, currentColor)
            var(--session-replay-scrollbar-track, transparent);
        }
      }

      /* A blank line still owes its row; without a floor it collapses and the
         transcript closes up wherever its author left a gap. 1lh rather than
         1em so a blank row is the SAME height as a printed one — the height
         above multiplies by line boxes, and a blank row an em tall under 1.6
         leading makes the reserved height wrong by the difference, once per
         gap. */
      .blank {
        min-height: 1lh;
      }

      /* The prompt is pinned BELOW the scroll area rather than living inside
         it. That is the shape of the TUI this replays: output accumulates
         upward and the cursor never moves off the bottom row. Its height comes
         from a property set at bind time, like every other height here, so a
         panel that shows one costs no layout when it appears. */
      /* The prompt sits OUTSIDE the scrollport, so with the host no longer
         padded it would otherwise sit flush against the panel's left and
         bottom borders. It takes the same inline inset as the screen above it,
         which is also what keeps the caret column aligned with the transcript
         it is answering. */
      #prompt {
        display: flex;
        align-items: baseline;
        gap: var(--session-replay-prompt-gap, 0.6ch);
        min-height: calc(var(--session-replay-prompt-rows, 0) * 1lh);
        padding-inline: var(--session-replay-padding-inline, 0px);
        padding-block-end: var(--session-replay-padding-block, 0px);
        padding-top: var(--session-replay-prompt-space, 0px);
        border-top: var(--session-replay-prompt-rule, none);
        white-space: pre;
      }

      /* Air between one command and the next.
         In a terminal the commands run together because the scrollback is a
         single stream; in a figure the reader is scanning for the boundaries,
         and the boundary is exactly where a banded block begins after an
         unbanded row. Nothing is inserted into the transcript to achieve it —
         no blank line that tsh never printed — it is margin on the first row
         of each block, and the height above reserves room for it. */
      .line:not(.banded) + .line.banded {
        margin-block-start: var(--session-replay-band-gap, 0px);
      }

      #prompt[hidden] {
        display: none;
      }

      /* The label is a MODE badge, not a sigil. tsh draws its current mode as
         dark ink on a light ground rather than as a bare character, so the
         label takes an optional ground and padding of its own; both default to
         nothing, which leaves a plain sigil looking exactly as it did. */
      #prompt-label {
        color: var(--session-replay-prompt-color, currentColor);
        background: var(--session-replay-prompt-bg, transparent);
        padding: var(--session-replay-prompt-label-padding, 0);
        border-radius: var(--session-replay-prompt-label-radius, 0);
        font-weight: var(--session-replay-prompt-weight, 700);
      }

      #prompt-input {
        flex: 1;
        min-width: 0;
      }

      #caret {
        display: inline-block;
        width: var(--session-replay-caret-width, 0.6em);
        height: var(--session-replay-caret-height, 1em);
        background: currentColor;
        vertical-align: text-bottom;
        animation: session-replay-blink 1.06s steps(1, end) infinite;
      }

      #caret[hidden] {
        display: none;
      }

      @keyframes session-replay-blink {
        0%, 50% { opacity: 1; }
        50.01%, 100% { opacity: 0; }
      }

      /* A reader who asked for less motion gets a steady caret, not a
         flashing one — the engine already gives them the whole transcript at
         once, and a blink is the half that would survive that. */
      @media (prefers-reduced-motion: reduce) {
        #caret {
          animation: none;
        }
      }
    `,
  ];

  static $html = html`
    <div id="screen"></div>
    <div id="prompt" hidden>
      <span id="prompt-label"></span>
      <span id="prompt-input"></span>
    </div>
    <span id="caret" hidden></span>
  `;
}

export namespace SessionReplay {
  // The colour vocabulary lives in `session-replay-ansi`, which is pure and
  // therefore unit-tested; re-exported here so a caller has one import.
  export type Sgr = AnsiSgr;
  export type Span = AnsiSpan;

  export interface Line {
    /** The line with every escape removed — what a caller that ignores colour wants. */
    text: string;
    mode: "typed" | "printed" | "blank";
    /** The same text, cut into runs by the colour in force. */
    spans: Span[];
    /**
     * The ground for the WHOLE row, when every run shares one — a terminal
     * band. Null when the row has no ground or changes ground partway.
     *
     * Computed once at parse rather than at paint, because the layout needs it
     * before anything is drawn: a run of banded rows is one visual block, and
     * the space between blocks has to be counted into the reserved height.
     */
    bg: Option<string>;
  }

  /** Milliseconds. `work` is the pause before a printed line — a program that
   * answered instantly reads as a canned string rather than as work. */
  export interface Timing {
    char: number;
    work: number;
    blank: number;
  }

  export interface ViewModel {
    sample: string;
    /** Null takes the component's own plain row. */
    $line: Option<(line: Line) => HTMLElement>;
    /** Null takes {@link DEFAULT_TIMING}. */
    timing: Option<Timing>;
    /**
     * A prompt pinned to the bottom, e.g. `"$"` or `"oc>"`.
     *
     * Null keeps the older shape, where a typed line is just another row in
     * the scroll area — which is right for a transcript being quoted, and
     * wrong for a session being shown.
     */
    prompt: Option<string>;
  }

  // 22ms a character is a fast human and a slow machine, which is the register
  // a demo wants: quick enough that nobody waits out a sentence, slow enough
  // that the eye reads it as typed rather than pasted. 340ms is the beat
  // before a printed result — the pause that makes it read as work.
  export const DEFAULT_TIMING: Timing = { char: 22, work: 340, blank: 90 };

  const TYPED = /^\s*[$>]\s/;

  // The one ground shared by every run on a line lives with the painter it
  // feeds — the term-row primitive.
  const rowBackground = TermRow.rowBackground;

  /**
   * The mechanics, separable from the component because two callers already
   * drive them from inside their own shadow roots.
   */
  export class Engine {
    /**
     * Called once the last line is on screen.
     *
     * A settable field rather than a constructor argument on purpose: pi and
     * herdr construct this with four arguments through the @tento-brand/kit
     * alias, and a fifth parameter would break two measured ports to add a
     * hook neither uses.
     */
    onSettled: Option<() => void> = null;
    /**
     * When set, typed lines are typed HERE — a prompt pinned below the screen —
     * instead of inline in the scroll area. Settable for the same reason
     * `onSettled` is: pi and herdr construct this with four arguments.
     */
    $promptInput: Option<HTMLElement> = null;
    private lines: Line[] = [];
    private timer: Option<number> = null;
    private observer: Option<IntersectionObserver> = null;
    private started = false;

    constructor(
      private readonly $screen: HTMLElement,
      private readonly $caret: HTMLElement,
      private readonly $line: (line: Line) => HTMLElement,
      private readonly timing: Timing,
    ) {}

    /** Read a transcript's own shape. Text is never altered — in pi's /tree the
     * indentation IS the drawing. */
    static parse(sample: string): Line[] {
      return sample.split("\n").map((raw) => {
        const { text, spans } = decodeAnsi(raw);
        return {
          text,
          spans,
          mode: text.trim() === "" ? "blank" : TYPED.test(text) ? "typed" : "printed",
          bg: rowBackground(spans),
        };
      });
    }

    /**
     * How many times a banded block STARTS after an unbanded row.
     *
     * Each of those boundaries gets a gap, and the screen reserves height for
     * the gaps as well as the rows — otherwise adding air between two commands
     * would push the last line out of a panel sized to exactly its row count.
     */
    get gapCount(): number {
      let gaps = 0;
      for (let i = 1; i < this.lines.length; i++) {
        if (this.lines[i].bg !== null && this.lines[i - 1].bg === null) gaps++;
      }
      return gaps;
    }

    bind(sample: string): void {
      this.lines = Engine.parse(sample);
      this.stop();
      this.started = false;
      this.$screen.replaceChildren();
      this.$caret.toggleAttribute("hidden", true);
      if (this.$promptInput) this.$promptInput.textContent = "";
    }

    get lineCount(): number {
      return this.lines.length;
    }

    /**
     * Play once, when `$host` is on screen and not before — and never again.
     *
     * Sections are commonly gated behind an IntersectionObserver, so a replay
     * that ran on load would be a recording of nothing for a reader three
     * screens above it. Playing once is the other half: a figure that restarts
     * every time it re-enters the viewport turns a demonstration into a fidget.
     */
    arm($host: Element): void {
      if (this.started || this.lines.length === 0) return;

      // A reader who asked for less motion gets the session, not the typing.
      // The transcript is the content and the replay is the design; this is the
      // one place they separate cleanly, so it is the one place that has to.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.started = true;
        this.$screen.replaceChildren(...this.lines.map((line) => this.$line(line)));
        this.$caret.removeAttribute("hidden");
        if (this.$promptInput) this.$promptInput.appendChild(this.$caret);
        this.onSettled?.();
        return;
      }

      this.disarm();
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.disarm();
        this.play();
      }, { threshold: 0.25 });
      this.observer.observe($host);
    }

    private play(): void {
      if (this.started) return;
      this.started = true;
      this.$screen.replaceChildren();
      this.$caret.removeAttribute("hidden");
      this.step(0, 0);
    }

    /**
     * One scheduled beat: the next character of a line being typed, or the next
     * whole line being printed.
     *
     * A chain of timeouts rather than one interval, because the beats are not
     * the same length — a character is ~22ms and a printed result is ~340 — and
     * an interval fast enough for the former has to count ticks for the latter.
     */
    private step(index: number, chars: number): void {
      if (index >= this.lines.length) {
        this.timer = null;
        this.onSettled?.();
        return;
      }
      const line = this.lines[index];

      if (line.mode === "typed") {
        // With a pinned prompt the marker is drawn by the prompt label, so the
        // transcript's own `$ ` would be printed twice — strip it while typing
        // and keep it on the line that lands in the scrollback, which is where
        // a real terminal shows it.
        const $prompt = this.$promptInput;
        // Trailing whitespace is never TYPED, only padded.
        //
        // A line captured from a terminal is padded with spaces out to the last
        // column, because that is how the band behind it gets its width. Typing
        // those spaces one beat at a time walks the caret steadily rightwards
        // across an empty prompt long after the command has finished — which
        // reads as a cursor drifting on its own, and was reported as exactly
        // that. The row that lands in the scrollback keeps its full width; only
        // the characters a person would have pressed are typed.
        const source = $prompt ? line.text.replace(TYPED, "") : line.text;
        const typed = source.replace(/\s+$/, "");
        const next = chars + 1;

        if ($prompt) {
          $prompt.textContent = typed.slice(0, next);
          $prompt.appendChild(this.$caret);
        } else {
          const $row = chars === 0
            ? this.$screen.appendChild(this.$line({ ...line, text: "", spans: [] }))
            : (this.$screen.lastElementChild as HTMLElement);
          $row.textContent = typed.slice(0, next);
          // The caret rides the end of the line being typed. Appended to the
          // SCREEN it would be a sibling of the rows and earn a line box of its
          // own, which added a nineteenth row to an eighteen-row transcript and
          // grew the figure the moment the replay started.
          $row.appendChild(this.$caret);
        }

        const done = next >= typed.length;
        if (done && $prompt) {
          // Submitted: the finished command moves up into the scrollback and
          // the prompt empties, with the caret staying where a caret belongs.
          this.$screen.appendChild(this.$line(line));
          $prompt.textContent = "";
          $prompt.appendChild(this.$caret);
        }
        this.follow();
        this.timer = window.setTimeout(
          () => (done ? this.step(index + 1, 0) : this.step(index, next)),
          this.timing.char,
        );
        return;
      }

      const $printed = this.$screen.appendChild(this.$line(line));
      // The caret only follows output when there is nowhere else for it to be.
      // With a prompt pinned below, output appearing above it must not drag the
      // cursor up out of the prompt — in a shell the caret waits at the prompt
      // while the program writes.
      if (!this.$promptInput) $printed.appendChild(this.$caret);
      this.follow();
      this.timer = window.setTimeout(
        () => this.step(index + 1, 0),
        line.mode === "blank" ? this.timing.blank : this.timing.work,
      );
    }

    /**
     * Keep the caret in view when the transcript is taller than its cap.
     *
     * Assignment rather than `scrollIntoView`, which scrolls every scrollable
     * ANCESTOR — including the document — and would drag the reader's page to
     * the figure the moment a capped replay started. Writing `scrollTop`
     * touches this element and nothing above it. On a screen shorter than its
     * cap `scrollHeight` equals the client height and this is a no-op.
     */
    private follow(): void {
      this.$screen.scrollTop = this.$screen.scrollHeight;
    }

    stop(): void {
      if (this.timer !== null) window.clearTimeout(this.timer);
      this.timer = null;
      this.disarm();
    }

    private disarm(): void {
      this.observer?.disconnect();
      this.observer = null;
    }
  }
}
